import React from 'react';

export default function Login({
  form,
  setForm,
  onSubmit,
  onSwitchToSignup,
  onForgotPassword,
  onGoogleSignIn,
  error,
  loading,
}) {
  return (
    <div className="auth-view">
      <div className="hero">
        <h2>Welcome back</h2>
        <p>Sign in to your TrailerCast account.</p>
      </div>

      {/* Google sign-in lives ABOVE the password form. Standard UX
          pattern (Stripe, Vercel, Linear): SSO at the top, "or use
          email" divider, then password fields. Users who already
          have a Google session save a password lookup; users who
          don't can scroll past. */}
      {onGoogleSignIn && (
        <>
          <GoogleSignInButton onClick={onGoogleSignIn} disabled={loading} label="Continue with Google" />
          <AuthDivider />
        </>
      )}

      <form className="auth-form" onSubmit={onSubmit}>
        <div className="auth-field">
          <label>Email</label>
          <input
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </div>

        <div className="auth-field">
          <label>Password</label>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Your password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={8}
          />
        </div>

        {error && (
          <div className="error-message">
            <span></span> {error}
          </div>
        )}

        <button type="submit" className="generate-btn ready" disabled={loading}>
          <span className="btn-icon"></span>
          <span className="btn-text">{loading ? 'Signing in…' : 'Sign in'}</span>
        </button>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
        <button className="auth-toggle" onClick={onForgotPassword} style={{ flex: 1 }}>
          Forgot password?
        </button>
        <button className="auth-toggle" onClick={onSwitchToSignup} style={{ flex: 1 }}>
          Need an account? Sign up
        </button>
      </div>
    </div>
  );
}

// ─── Google Sign-In button + "or" divider ──────────────────────────
//
// Lifted to the bottom of this file so SignupStep1 can import + reuse
// without us re-implementing the same markup twice. Styling matches
// Google's 2024 Sign-In branding guidelines (white surface, Google G
// glyph, "Continue with Google" label, system font).

export function GoogleSignInButton({ onClick, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="google-signin-btn"
      style={{
        width: '100%',
        height: 44,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        background: '#fff',
        color: '#1f1f1f',
        border: '1px solid #dadce0',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
        boxShadow: '0 1px 2px rgba(60, 64, 67, 0.08)',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = '#f8f9fa';
        e.currentTarget.style.borderColor = '#c8ccd0';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(60, 64, 67, 0.16)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#fff';
        e.currentTarget.style.borderColor = '#dadce0';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(60, 64, 67, 0.08)';
      }}
      aria-label={label}
    >
      {/* Google G logo — official 4-color SVG (red/yellow/green/blue) */}
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
          d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
          fill="#4285F4"
        />
        <path
          d="M9 18c2.43 0 4.467-.806 5.956-2.183l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
          fill="#34A853"
        />
        <path
          d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
          fill="#FBBC05"
        />
        <path
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
          fill="#EA4335"
        />
      </svg>
      <span>{label}</span>
    </button>
  );
}

export function AuthDivider() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '16px 0',
      }}
      aria-hidden="true"
    >
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
      <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        or
      </span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}
